/** Pure helpers for Work Brief guided handoff UX (no React). */

export const CATEGORY_LABELS = {
  missing_actor: "Who does this?",
  missing_condition: "When does this apply?",
  missing_outcome: "What should success look like?",
  missing_failure_behavior: "What happens when it fails?",
  missing_threshold: "What number or limit?",
  missing_timeframe: "By when / how long?",
  missing_non_goal: "What is out of scope?",
  missing_validation: "How will we prove it works?",
  missing_rollout: "How should this roll out?",
  missing_data_handling: "How should data and access work?",
  unsupported_claim: "Needs clarification",
  ambiguous_scope: "Scope unclear",
  other: "Needs clarification",
};

export const SEVERITY_LABELS = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

const CATEGORY_RULES = [
  {
    category: "unsupported_claim",
    pattern:
      /\bunsupported\b|\bcannot verify\b|\bno (?:source|evidence)\b|\bnot (?:stated|supported|grounded)\b|\binvent(?:ed|ing)?\b/i,
  },
  {
    category: "ambiguous_scope",
    pattern:
      /\bambiguous\b|\bunclear (?:whether|which|scope)\b|\bwhich (?:scope|users?|repos?|paths?)\b|\bscope\b|\ball users\b|\bevery (?:user|request|case)\b/i,
  },
  {
    category: "missing_data_handling",
    pattern:
      /\b(?:sensitive|private|personal|customer) data\b|\bretention\b|\baudit(?:ed| log)?\b|\bpermission\b|\bwho (?:can|may) access\b|\bexpos(?:e|ed|ure)\b|\bcredential/i,
  },
  {
    category: "missing_failure_behavior",
    pattern:
      /\bwhat (?:if|happens when)\b|\bfail(?:s|ure|ed)\b|\berror (?:case|behavior|response)\b|\bretr(?:y|ies)\b|\brecover(?:y|able)?\b|\bempty state\b|\bdenied\b|\bduplicate\b/i,
  },
  {
    category: "missing_validation",
    pattern:
      /\bhow (?:do|will|should) (?:we|product|engineering) (?:verify|validate|test|prove|confirm)\b|\bacceptance (?:check|test|evidence)\b|\bwhat (?:test|evidence|proof)\b/i,
  },
  {
    category: "missing_outcome",
    pattern:
      /\bwhat should (?:happen|the user see|be returned)\b|\bexpected (?:result|outcome|behavior)\b|\bsuccess(?:ful)? (?:result|outcome|behavior)\b|\bhow (?:should|will) .* know .* work/i,
  },
  {
    category: "missing_non_goal",
    pattern:
      /\bwhat (?:is|isn't|is not) (?:out of scope|included)\b|\bnon-goal\b|\bout of scope\b|\bshould (?:we|this) avoid\b|\bnot include/i,
  },
  {
    category: "missing_rollout",
    pattern:
      /\brollout\b|\bfeature flag\b|\bgradual(?:ly)?\b|\bbackward compatib\w*\b|\bmigrat(?:e|ion)\b|\brollback\b|\bexisting users?\b/i,
  },
  {
    category: "missing_threshold",
    pattern:
      /\bthreshold\b|\bhow many\b|\bpercent(?:age)?\b|%|\bSLA\b|\bat least\b|\bmaximum\b|\bminimum\b|\blimit\b|\bhow much\b/i,
  },
  {
    category: "missing_timeframe",
    pattern:
      /\bTTL\b|\btimeout\b|\bdeadline\b|\bduration\b|\bhow long\b|\btimeframe\b|\bexpire(?:s|d|ation)?\b|\bwithin\b|\b\d+\s*(?:ms|s|sec|seconds?|m|min|minutes?|h|hours?|d|days?)\b/i,
  },
  {
    category: "missing_actor",
    pattern: /\bwho\b|\bactor\b|\bwhich (?:user|role|actor)\b|\bfor whom\b|\bwhose\b/i,
  },
  {
    category: "missing_condition",
    pattern: /\bwhen\b|\bunless\b|\bcondition\b|\bunder what\b|\bif\b/i,
  },
];

export function classifyQuestionText(text) {
  const t = String(text || "").trim();
  if (!t) return "other";
  for (const rule of CATEGORY_RULES) {
    if (rule.pattern.test(t)) return rule.category;
  }
  return "other";
}

export function deriveSeverity(category) {
  switch (category) {
    case "unsupported_claim":
    case "ambiguous_scope":
    case "missing_threshold":
    case "missing_timeframe":
    case "missing_outcome":
    case "missing_failure_behavior":
    case "missing_validation":
    case "missing_data_handling":
      return "high";
    case "missing_actor":
    case "missing_condition":
    case "missing_non_goal":
    case "missing_rollout":
      return "medium";
    default:
      return "low";
  }
}

export function originLabel(origin) {
  if (origin === "human_added") return "Added by you";
  if (origin === "deterministic_scaffold") return "Copied from your request";
  if (origin === "model") return "Suggested from your request";
  if (origin) return "From your request";
  return "Needs review";
}

export function sourceTrustLabel(origin) {
  if (origin === "human_added") return "You added";
  if (origin === "deterministic_scaffold") return "Exact source";
  if (origin === "model") return "Needs review";
  return "Needs review";
}

export function isHumanAddedCriterionId(criterionId) {
  return String(criterionId || "").startsWith("crit-human-");
}

export function isHumanAddedAssumptionId(assumptionId) {
  return String(assumptionId || "").startsWith("note-human-");
}

export function isHumanAddedQuestionId(questionId) {
  return String(questionId || "").startsWith("q-human-");
}

/** Content payload fields for revise / create from a version document. */
export function contractToContentBody(contract) {
  if (!contract) return null;
  return {
    title: contract.title || "Handoff contract",
    summary: contract.summary || "",
    ...(Array.isArray(contract.summarySourceClauseIds)
      ? { summarySourceClauseIds: contract.summarySourceClauseIds }
      : { summarySourceClauseIds: [] }),
    sourceClauses: Array.isArray(contract.sourceClauses)
      ? contract.sourceClauses.map((c) => ({
          clauseId: c.clauseId,
          text: c.text,
          order: typeof c.order === "number" ? c.order : 0,
        }))
      : [],
    sourceReferences: Array.isArray(contract.sourceReferences)
      ? contract.sourceReferences
      : [],
    criteria: Array.isArray(contract.criteria) ? contract.criteria : [],
    assumptions: Array.isArray(contract.assumptions) ? contract.assumptions : [],
    nonGoals: Array.isArray(contract.nonGoals) ? contract.nonGoals : [],
    protectedBehaviors: Array.isArray(contract.protectedBehaviors)
      ? contract.protectedBehaviors
      : [],
    unresolvedQuestions: Array.isArray(contract.unresolvedQuestions)
      ? contract.unresolvedQuestions
      : [],
    verificationRequirements: Array.isArray(contract.verificationRequirements)
      ? contract.verificationRequirements
      : [],
  };
}

function nextOrder(clauses) {
  if (!clauses.length) return 1;
  return (
    Math.max(
      ...clauses.map((c) => (typeof c.order === "number" ? c.order : 0))
    ) + 1
  );
}

function uniqueId(prefix) {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Strip accidental backslash escapes that sometimes appear in founder-facing
 * handoff text (e.g. "forever\\\\\\\\." or "seconds\\\\\\.").
 */
export function cleanFounderDisplayText(text) {
  let out = String(text || "");
  // Repeatedly unwrap escaped punctuation / doubled backslashes (multi-escape junk).
  for (let i = 0; i < 8; i++) {
    const next = out
      .replace(/\\+([.!?,;:'"”’)\]}])/g, "$1")
      .replace(/\\{2,}/g, "")
      .replace(/\\+$/g, "");
    if (next === out) break;
    out = next;
  }
  return out.replace(/[^\S\n]+/g, " ").trim();
}

/**
 * Append a human-authored promise as a cited V2 criterion + source clause.
 * Uses the user's text as the exact source sentence (honest grounding).
 * Actor/action/outcomes copy that text (actor truncated only if over schema max).
 */
export function appendHumanPromiseToContent(content, promiseTextRaw) {
  const text = cleanFounderDisplayText(promiseTextRaw);
  if (!text) throw new Error("Promise text is required.");
  const body = { ...content };
  const clauseId = uniqueId("clause-human-");
  const criterionId = uniqueId("crit-human-");
  const clauses = [...(body.sourceClauses || [])];
  clauses.push({ clauseId, text, order: nextOrder(clauses) });
  const criteria = [...(body.criteria || [])];
  const actorMax = 200;
  criteria.push({
    criterionId,
    actor: text.length > actorMax ? text.slice(0, actorMax) : text,
    preconditions: [],
    action: text,
    expectedOutcomes: [text],
    forbiddenOutcomes: [],
    criterionType: "behavioral",
    requiredEvidenceTier: "moderate",
    blockingPolicy: "blocking",
    sourceClauseIds: [clauseId],
  });
  // Keep summary citations valid if empty.
  const summarySourceClauseIds = Array.isArray(body.summarySourceClauseIds)
    ? body.summarySourceClauseIds
    : [];
  return {
    ...body,
    sourceClauses: clauses,
    criteria,
    summarySourceClauseIds:
      summarySourceClauseIds.length > 0
        ? summarySourceClauseIds
        : clauses[0]
          ? [clauses[0].clauseId]
          : [],
  };
}

/**
 * Append an engineer/product-offered open question (V2 unresolved question + source clause).
 * Becomes a real server question that can block Strict until answered / not required.
 */
export function appendHumanQuestionToContent(content, questionTextRaw) {
  const text = cleanFounderDisplayText(questionTextRaw);
  if (!text) throw new Error("Question text is required.");
  const body = { ...content };
  const clauseId = uniqueId("clause-question-");
  const questionId = uniqueId("q-human-");
  const clauses = [...(body.sourceClauses || [])];
  clauses.push({ clauseId, text, order: nextOrder(clauses) });
  const unresolvedQuestions = [...(body.unresolvedQuestions || [])];
  unresolvedQuestions.push({
    questionId,
    text,
    status: "open",
    relatedSourceClauseIds: [clauseId],
  });
  const summarySourceClauseIds = Array.isArray(body.summarySourceClauseIds)
    ? body.summarySourceClauseIds
    : [];
  return {
    ...body,
    sourceClauses: clauses,
    unresolvedQuestions,
    summarySourceClauseIds:
      summarySourceClauseIds.length > 0
        ? summarySourceClauseIds
        : clauses[0]
          ? [clauses[0].clauseId]
          : [],
  };
}

/**
 * Append a human note as a V2 assumption citing a new source clause (exact text).
 */
export function appendHumanNoteToContent(content, noteTextRaw) {
  const text = cleanFounderDisplayText(noteTextRaw);
  if (!text) throw new Error("Note text is required.");
  const body = { ...content };
  const clauseId = uniqueId("clause-note-");
  const assumptionId = uniqueId("note-human-");
  const clauses = [...(body.sourceClauses || [])];
  clauses.push({ clauseId, text, order: nextOrder(clauses) });
  const assumptions = [...(body.assumptions || [])];
  assumptions.push({
    assumptionId,
    text,
    risk: "low",
    sourceClauseIds: [clauseId],
  });
  const summarySourceClauseIds = Array.isArray(body.summarySourceClauseIds)
    ? body.summarySourceClauseIds
    : [];
  return {
    ...body,
    sourceClauses: clauses,
    assumptions,
    summarySourceClauseIds:
      summarySourceClauseIds.length > 0
        ? summarySourceClauseIds
        : clauses[0]
          ? [clauses[0].clauseId]
          : [],
  };
}

function collectReferencedClauseIds(content) {
  const ids = new Set();
  const add = (list) => {
    if (!Array.isArray(list)) return;
    for (const id of list) {
      if (id) ids.add(String(id));
    }
  };
  add(content.summarySourceClauseIds);
  for (const c of content.criteria || []) add(c.sourceClauseIds);
  for (const a of content.assumptions || []) add(a.sourceClauseIds);
  for (const n of content.nonGoals || []) add(n.sourceClauseIds);
  for (const p of content.protectedBehaviors || []) add(p.sourceClauseIds);
  for (const q of content.unresolvedQuestions || []) add(q.relatedSourceClauseIds);
  for (const v of content.verificationRequirements || []) add(v.sourceClauseIds);
  return ids;
}

/**
 * Remove a promise (criterion) via successor revision content.
 * Drops now-unreferenced source clauses and verification links.
 */
export function removePromiseFromContent(content, criterionId) {
  const id = String(criterionId || "").trim();
  if (!id) throw new Error("Promise id is required.");
  const body = { ...content };
  const before = Array.isArray(body.criteria) ? body.criteria : [];
  const removed = before.find((c) => c.criterionId === id);
  if (!removed) throw new Error("Promise not found on this handoff.");
  const criteria = before.filter((c) => c.criterionId !== id);
  const verificationRequirements = (body.verificationRequirements || [])
    .map((vr) => ({
      ...vr,
      criterionIds: (vr.criterionIds || []).filter((cid) => cid !== id),
    }))
    .filter((vr) => (vr.criterionIds || []).length > 0);
  const next = {
    ...body,
    criteria,
    verificationRequirements,
  };
  const referenced = collectReferencedClauseIds(next);
  next.sourceClauses = (body.sourceClauses || []).filter((c) =>
    referenced.has(String(c.clauseId))
  );
  if (Array.isArray(next.summarySourceClauseIds)) {
    next.summarySourceClauseIds = next.summarySourceClauseIds.filter((cid) =>
      referenced.has(String(cid))
    );
  }
  return next;
}

/**
 * Mark a promise not required: remove criterion and record a persisted engineer note.
 */
export function markPromiseNotRequiredInContent(content, criterionId, reasonRaw) {
  const reason = String(reasonRaw || "").trim();
  if (!reason) throw new Error("A short reason is required.");
  const removed = (content.criteria || []).find((c) => c.criterionId === criterionId);
  if (!removed) throw new Error("Promise not found on this handoff.");
  const promiseLabel = promiseText(removed);
  const without = removePromiseFromContent(content, criterionId);
  const noteText = `Not required for this handoff: ${promiseLabel}. Reason: ${reason}`;
  return appendHumanNoteToContent(without, noteText);
}

/**
 * Replace a promise's wording with exact-copy human text (new source clause).
 * Keeps the same criterionId so the handoff item stays stable.
 */
export function replacePromiseInContent(content, criterionId, promiseTextRaw) {
  const id = String(criterionId || "").trim();
  if (!id) throw new Error("Promise id is required.");
  const text = cleanFounderDisplayText(promiseTextRaw);
  if (!text) throw new Error("Promise text is required.");
  const body = { ...content };
  const before = Array.isArray(body.criteria) ? body.criteria : [];
  const existing = before.find((c) => c.criterionId === id);
  if (!existing) throw new Error("Promise not found on this handoff.");

  const clauseId = uniqueId("clause-human-");
  const clauses = [...(body.sourceClauses || [])];
  clauses.push({ clauseId, text, order: nextOrder(clauses) });
  const actorMax = 200;
  const criteria = before.map((c) => {
    if (c.criterionId !== id) return c;
    return {
      ...c,
      actor: text.length > actorMax ? text.slice(0, actorMax) : text,
      preconditions: Array.isArray(c.preconditions) ? c.preconditions : [],
      action: text,
      expectedOutcomes: [text],
      forbiddenOutcomes: Array.isArray(c.forbiddenOutcomes) ? c.forbiddenOutcomes : [],
      criterionType: c.criterionType || "behavioral",
      requiredEvidenceTier: c.requiredEvidenceTier || "moderate",
      blockingPolicy: c.blockingPolicy || "blocking",
      sourceClauseIds: [clauseId],
    };
  });
  const next = {
    ...body,
    sourceClauses: clauses,
    criteria,
  };
  const referenced = collectReferencedClauseIds(next);
  next.sourceClauses = clauses.filter((c) => referenced.has(String(c.clauseId)));
  if (Array.isArray(next.summarySourceClauseIds)) {
    next.summarySourceClauseIds = next.summarySourceClauseIds.filter((cid) =>
      referenced.has(String(cid))
    );
    if (next.summarySourceClauseIds.length === 0 && next.sourceClauses[0]) {
      next.summarySourceClauseIds = [next.sourceClauses[0].clauseId];
    }
  }
  return next;
}

/** Plain-language Feature Plan / Work Brief status for founders. */
export function humanizePlanStatus(status, error) {
  const code = String(error?.code || "").toUpperCase();
  const normalized = String(status || "").toLowerCase();
  if (normalized === "generating") {
    return {
      key: "generating",
      title: "Preparing engineering guidance…",
      summary:
        "The engineering implementation plan is still running. Product discovery can continue from the request and any reviewed screenshots. This plan is guidance, not a freeze gate.",
      badge: "Preparing guidance",
      recovery: null,
    };
  }
  if (normalized === "pending") {
    return {
      key: "pending",
      title: "Engineering guidance queued…",
      summary:
        code === "REDIS_UNAVAILABLE"
          ? "Planning is waiting because background jobs are unavailable. Try again later or retry planning. Discovery can still continue."
          : "The engineering implementation plan is queued. Product discovery can continue from the request and any reviewed screenshots.",
      badge: "Queued",
      recovery: "retry",
    };
  }
  if (normalized === "insufficient_evidence" || code === "INSUFFICIENT_EVIDENCE") {
    return {
      key: "insufficient_evidence",
      title: "We couldn’t make a confident engineering plan.",
      summary:
        "The request may be too broad, or the repo evidence did not point to a clear implementation area.",
      badge: "Needs a clearer request",
      recovery: "insufficient_evidence",
    };
  }
  if (normalized === "failed") {
    return {
      key: "failed",
      title: "Planning didn’t finish.",
      summary: error?.message
        ? String(error.message)
        : "Something went wrong while preparing the engineering plan. You can retry or revise the request.",
      badge: "Failed",
      recovery: "retry",
    };
  }
  if (normalized === "rejected") {
    return {
      key: "rejected",
      title: "This request doesn’t fit the repository well enough.",
      summary: "Revise the request to match what this codebase can support, then try again.",
      badge: "Needs revision",
      recovery: "revise_request",
    };
  }
  if (normalized === "completed") {
    return {
      key: "completed",
      title: "Engineering plan ready",
      summary: "You can review promises, approve the handoff, and continue to engineer decision.",
      badge: "Ready",
      recovery: null,
    };
  }
  return {
    key: normalized || "unknown",
    title: status || "Unknown status",
    summary: "",
    badge: status || "Unknown",
    recovery: null,
  };
}

export function isPlanPreparing(status) {
  const s = String(status || "").toLowerCase();
  return s === "generating" || s === "pending";
}

export function isPlanBlockedForHandoff(status) {
  const s = String(status || "").toLowerCase();
  return (
    s === "generating" ||
    s === "pending" ||
    s === "failed" ||
    s === "rejected" ||
    s === "insufficient_evidence"
  );
}

export const ENGINEERING_REJECTION_REASON_CODES = [
  { code: "scope_unclear", label: "Scope is unclear" },
  { code: "too_broad", label: "Too broad" },
  { code: "technically_risky", label: "Technically risky" },
  { code: "missing_detail", label: "Missing detail" },
  { code: "wrong_plan", label: "Wrong plan / wrong area" },
  { code: "conflicts_with_existing_behavior", label: "Conflicts with existing behavior" },
  { code: "other", label: "Other" },
];

function normalizePromisePart(text) {
  return cleanFounderDisplayText(
    String(text || "")
      .normalize("NFC")
      .trim()
      .replace(/\s+/g, " ")
  );
}

/**
 * Human-readable promise for the handoff UI.
 * Scaffolded exact-copy criteria often repeat the same sentence in actor/action/outcomes —
 * collapse that to one statement instead of "A A — A".
 */
export function promiseText(criterion) {
  if (!criterion) return "";
  const actor = normalizePromisePart(criterion.actor);
  const action = normalizePromisePart(criterion.action);
  const outcomeParts = Array.isArray(criterion.expectedOutcomes)
    ? criterion.expectedOutcomes.map(normalizePromisePart).filter(Boolean)
    : [];
  const outcomes = [...new Set(outcomeParts)].join("; ");

  // Exact-copy / founder scaffold: same sentence in every slot.
  if (action && action === actor && (!outcomes || outcomes === action)) {
    return action;
  }
  if (action && outcomes === action && (!actor || actor === action)) {
    return action;
  }
  if (!action && outcomes && (!actor || actor === outcomes)) {
    return outcomes;
  }

  // Distinct slots: lead with the commitment (action), add outcome only if new.
  if (action) {
    if (outcomes && outcomes !== action && !action.includes(outcomes)) {
      return `${action} Expected: ${outcomes}.`;
    }
    // Drop actor when it is just a role fragment already implied, or duplicates action.
    if (actor && actor !== action && !action.startsWith(actor) && actor.length <= 40) {
      return `${actor} ${action}`;
    }
    return action;
  }

  if (outcomes) return outcomes;
  if (actor) return actor;
  return "Promise";
}

export function clauseTextById(draft, clauseId) {
  const clauses = Array.isArray(draft?.sourceClauses) ? draft.sourceClauses : [];
  const match = clauses.find((c) => c.clauseId === clauseId);
  return match?.text || "";
}

export function promiseSourceSentence(criterion, draft) {
  const ids = Array.isArray(criterion?.sourceClauseIds) ? criterion.sourceClauseIds : [];
  for (const id of ids) {
    const text = cleanFounderDisplayText(clauseTextById(draft, id));
    if (text) return text;
  }
  return "";
}

export function icStorageKey(workBriefId) {
  return `intentContractLab:${workBriefId || "none"}`;
}

/**
 * @param {string} workBriefId
 * @param {string} [requestText] When provided, ignore stored ids bound to a different request.
 */
export function readStoredContractIds(workBriefId, requestText) {
  if (!workBriefId) return null;
  try {
    const raw = sessionStorage.getItem(icStorageKey(workBriefId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.contractId || !parsed?.versionId) return null;
    const current = String(requestText || "").trim();
    const bound = parsed.requestText != null ? String(parsed.requestText).trim() : null;
    // Stale binding: request changed since this draft was prepared.
    if (current && bound != null && bound !== current) return null;
    return {
      contractId: String(parsed.contractId),
      versionId: String(parsed.versionId),
      requestText: bound,
    };
  } catch {
    /* ignore */
  }
  return null;
}

export function writeStoredContractIds(workBriefId, ids) {
  if (!workBriefId || !ids?.contractId || !ids?.versionId) return;
  const payload = {
    contractId: String(ids.contractId),
    versionId: String(ids.versionId),
  };
  if (ids.requestText != null) {
    payload.requestText = String(ids.requestText);
  }
  sessionStorage.setItem(icStorageKey(workBriefId), JSON.stringify(payload));
}

/** Browser-only scratch notes — never treated as contract resolution. */
export function questionNotesStorageKey(workBriefId) {
  return `workBriefQuestionNotes:${workBriefId || "none"}`;
}

export function readQuestionNotes(workBriefId) {
  if (!workBriefId) return {};
  try {
    const raw = sessionStorage.getItem(questionNotesStorageKey(workBriefId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function writeQuestionNotes(workBriefId, notes) {
  if (!workBriefId) return;
  sessionStorage.setItem(questionNotesStorageKey(workBriefId), JSON.stringify(notes || {}));
}

function plural(n, singular, pluralWord = `${singular}s`) {
  return `${n} ${n === 1 ? singular : pluralWord}`;
}

/**
 * Derive stepper statuses with honest, non-blocking copy.
 * @returns {Array<{ id: string, label: string, status: string, detail: string }>}
 */
export function deriveStepperStatuses({
  hasRequest,
  hasDraft,
  promiseCount = 0,
  openQuestionCount = 0,
  contractState,
  approvalAllowed,
  policyMode = "advisory",
  hasEngAck,
  hasDeliveryBranch,
  hasAcceptanceReview,
  planStatus,
  hasEngineeringRejection = false,
  hasAcceptanceCriteria = false,
  /**
   * The server has recorded that these requirements cannot all hold. Applied as a
   * withdrawal over the steps below rather than woven into each branch, because that
   * is what it is: the local lifecycle happened, and the conclusions it supports have
   * stopped being safe.
   */
  requirementsConflict = false,
}) {
  const isApproved =
    contractState === "product_approved" ||
    contractState === "engineering_acknowledged";

  const request = {
    id: "request",
    label: "Request",
    status: hasRequest ? "complete" : "not_started",
    detail: hasRequest ? "Ready" : "Add a description or screenshots",
  };

  let draftStatus = "not_started";
  let draftDetail = "Not started";
  if (hasDraft && promiseCount === 0) {
    draftStatus = "needs_attention";
    draftDetail = "No promises found yet";
  } else if (hasDraft) {
    draftStatus = isApproved && !hasEngineeringRejection ? "complete" : "ready";
    draftDetail = `${plural(promiseCount, "promise")} · ${plural(openQuestionCount, "question")}`;
  } else if (String(planStatus || "").toLowerCase() === "insufficient_evidence") {
    draftStatus = "needs_attention";
    draftDetail = "Optional: clarify agreement while plan needs work";
  } else if (hasRequest) {
    draftStatus = "ready";
    draftDetail = "Extract promises from your request";
  }

  const promiseDraft = {
    id: "promise_draft",
    label: "Promise draft",
    status: draftStatus,
    detail: draftDetail,
  };

  let handoffStatus = "not_started";
  let handoffDetail = "Locked until a promise draft exists";
  if (isApproved && !hasEngineeringRejection) {
    handoffStatus = "complete";
    handoffDetail =
      openQuestionCount > 0 ? "Approved with warnings" : "Approved";
  } else if (hasEngineeringRejection) {
    handoffStatus = "needs_attention";
    handoffDetail = "Engineer requested changes — revise & re-approve";
  } else if (hasDraft && promiseCount === 0) {
    handoffStatus = "needs_attention";
    handoffDetail = "Needs at least one promise";
  } else if (hasDraft) {
    if (policyMode === "strict" && approvalAllowed === false) {
      handoffStatus = "needs_attention";
      handoffDetail = "Strict blocked";
    } else if (approvalAllowed === true || policyMode === "advisory") {
      handoffStatus = "ready";
      handoffDetail =
        openQuestionCount > 0 ? "Warnings allowed" : "Ready to approve";
    } else if (approvalAllowed === false) {
      handoffStatus = "needs_attention";
      handoffDetail = "Not ready";
    } else {
      handoffStatus = "ready";
      handoffDetail =
        openQuestionCount > 0 ? "Warnings allowed" : "Ready to approve";
    }
  }

  const handoff = {
    id: "handoff",
    label: "Handoff decision",
    status: handoffStatus,
    detail: handoffDetail,
  };

  let deliveryStatus = "not_started";
  let deliveryDetail = "Locked until product approval";
  if (hasEngineeringRejection) {
    deliveryStatus = "needs_attention";
    deliveryDetail = "Engineer requested changes";
  } else if (hasEngAck && hasDeliveryBranch) {
    deliveryStatus = "complete";
    deliveryDetail = "Accepted — branch selected";
  } else if (hasEngAck) {
    deliveryStatus = "ready";
    deliveryDetail = "Accepted — add delivery branch";
  } else if (isApproved) {
    deliveryStatus = "ready";
    deliveryDetail = "Next: engineer accepts or requests changes";
  }

  const delivery = {
    id: "delivery",
    label: "Engineer decision",
    status: deliveryStatus,
    detail: deliveryDetail,
  };

  let reviewStatus = "not_started";
  let reviewDetail = "Locked until the engineer accepts and a branch is selected";
  if (hasAcceptanceReview) {
    reviewStatus = "complete";
    reviewDetail = "Complete";
  } else if (!hasEngAck) {
    reviewStatus = "not_started";
    reviewDetail = "Locked until the engineer accepts the handoff";
  } else if (!hasDeliveryBranch) {
    reviewStatus = "not_started";
    reviewDetail = "Locked until a delivery branch is selected";
  } else if (!hasAcceptanceCriteria) {
    reviewStatus = "needs_attention";
    reviewDetail = "Locked — Task has no acceptance criteria to review against";
  } else {
    reviewStatus = "ready";
    reviewDetail = "Ready to review delivered branch";
  }

  const review = {
    id: "review",
    label: "Review delivered branch",
    status: reviewStatus,
    detail: reviewDetail,
  };

  const steps = [request, promiseDraft, handoff, delivery, review];
  return requirementsConflict ? withdrawConflictedSteps(steps) : steps;
}

/**
 * Details for a step whose claim the conflict withdraws. Two readings per step: one
 * for a claim already made, which has to be withdrawn rather than denied, and one for
 * a step that was about to be offered.
 */
const CONFLICT_STEP_DETAIL = {
  promise_draft: { reached: "Promises conflict", pending: "Promises conflict" },
  handoff: {
    reached: "Approved before the conflict was found",
    pending: "Settle the conflict first",
  },
  delivery: {
    reached: "Accepted before the conflict was found",
    pending: "Settle the conflict first",
  },
  review: {
    reached: "Out of date — requirements conflict",
    pending: "Settle the conflict first",
  },
};

function withdrawConflictedSteps(steps) {
  return steps.map((step) => {
    // The request itself is not in question, and a step still locked is not claiming
    // anything that needs withdrawing.
    const detail = CONFLICT_STEP_DETAIL[step.id];
    if (!detail || (step.status !== "complete" && step.status !== "ready")) return step;
    return {
      ...step,
      status: "needs_attention",
      detail: step.status === "complete" ? detail.reached : detail.pending,
    };
  });
}

export function statusLabel(status) {
  switch (status) {
    case "complete":
      return "Complete";
    case "ready":
      return "Ready";
    case "needs_attention":
      return "Needs attention";
    default:
      return "Not started";
  }
}

export function humanizeReadinessBlockers(readiness) {
  const codes = Array.isArray(readiness?.blockingPolicyCodes)
    ? readiness.blockingPolicyCodes
    : [];
  const hard = Array.isArray(readiness?.hardFailures) ? readiness.hardFailures : [];
  const reasons = [];
  for (const code of codes) {
    if (code === "open_unresolved_questions" || code === "high_severity_open_questions") {
      reasons.push("Strict handoff is blocked because questions remain.");
    } else if (code === "empty_criteria") {
      reasons.push(
        "This is not ready to hand off until there is at least one promise."
      );
    } else if (
      code === "dangling_clause_id" ||
      code === "missing_source_clause" ||
      code === "empty_expected_outcomes" ||
      code === "suggestion_shaped_as_requirement"
    ) {
      reasons.push("The promise draft has citation problems that must be fixed.");
    } else {
      reasons.push(code.replace(/_/g, " "));
    }
  }
  for (const issue of hard) {
    if (issue?.message && !reasons.some((r) => r.includes(issue.message))) {
      reasons.push(issue.message);
    }
  }
  return [...new Set(reasons)];
}

export function humanizeWarnings(readiness) {
  const warnings = Array.isArray(readiness?.warnings) ? readiness.warnings : [];
  return warnings.map((w) => {
    if (w.code === "open_unresolved_questions") {
      return "Some questions are still open (accepted as warnings under Advisory).";
    }
    if (w.code === "empty_criteria") {
      return "This is not ready to hand off until there is at least one promise.";
    }
    if (w.code === "high_severity_open_questions") {
      return "Some open questions look high priority.";
    }
    return w.message || w.code || "Warning";
  });
}

export function isBlockedOnlyByOpenQuestions(readiness) {
  if (!readiness || readiness.approvalAllowed !== false) return false;
  const codes = Array.isArray(readiness.blockingPolicyCodes)
    ? readiness.blockingPolicyCodes
    : [];
  if (!codes.length) return false;
  const questionCodes = new Set([
    "open_unresolved_questions",
    "high_severity_open_questions",
  ]);
  return codes.every((c) => questionCodes.has(c));
}
